<template>
  <el-dialog
    v-model="dialogVisible"
    width="800px"
    destroy-on-close
    append-to-body
    center
    title="版本更新历史"
  >
    <div v-loading="loading">
      <div class="version-list-scroll">
        <div v-if="filteredVersionList.length > 0">
          <div v-for="(item, idx) in filteredVersionList" :key="item.systemVersionId" class="mb-4">
            <div>
              <div class="mb-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="text-lg font-bold">{{ item.systemVersion }}</span>
                    <el-tag v-if="item.systemVersionId === currentSystemVersionId" type="primary">当前版本</el-tag>
                  </div>
                  <span class="text-sm text-gray-500">{{ item.systemUpdateTime }}</span>
                </div>
              </div>

              <div v-if="item.noticeContent" class="mb-3">
                <div v-html="renderMarkdown(item.noticeContent)" />
              </div>
            </div>

            <el-divider v-if="idx < filteredVersionList.length - 1" />
          </div>

          <!-- 分页 -->
          <div v-if="total > pageSize" class="mt-4 flex justify-center">
            <el-pagination
              v-model:current-page="currentPage"
              v-model:page-size="pageSize"
              :page-sizes="[10, 20, 50]"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            />
          </div>
        </div>
        <div v-else-if="!loading" class="py-8 text-center text-gray-500">
          暂无更新记录
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-center">
        <el-button @click="handleClose" type="primary" style="width: 325px; height: 46px;background: linear-gradient(180deg, #1E6CEB 0%, #1155C5 100%);border-radius: 4px;box-shadow: 2px 2px 8px 0 rgb(0 82 217 / 25%);">我知道了</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import type { SystemVersion } from "@repo/codegen/teashop-base-api"
import { useVModel } from "@vueuse/core"
import { ElDialog, ElPagination } from "element-plus"
import { marked } from "marked"
import { storeToRefs } from "pinia"
import semver from "semver"
import { computed, ref, watch } from "vue"
import { systemVersionAPI } from "@/api/teashop-base"
import { useAppStore } from "@/store/modules/app"
import { SystemTypeConstants } from "@/types/platformType"

const emit = defineEmits(["update:modelValue"])

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
})

const dialogVisible = useVModel(props, "modelValue", emit, { passive: true })
const systemVersionList = ref<SystemVersion[]>([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

const renderMarkdown = (text: string) => {
  return marked(text)
}

const getVersionHistory = async () => {
  if (!dialogVisible.value) return

  loading.value = true
  try {
    const res = await systemVersionAPI.systemVersionPage({
      page: { current: currentPage.value, size: pageSize.value },
      systemVersion: {
        systemType: SystemTypeConstants.B_CASHIER,
      },
    })

    if (res.data.code === 0 && res.data.data) {
      systemVersionList.value = res.data.data.records?.map(item => item.systemVersion!).filter(Boolean) || []
      total.value = res.data.data.total || 0
    }
  } catch (error) {
    console.error("获取版本历史失败:", error)
  } finally {
    loading.value = false
  }
}

const handleSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  getVersionHistory()
}

const handleCurrentChange = (page: number) => {
  currentPage.value = page
  getVersionHistory()
}

const handleClose = () => {
  dialogVisible.value = false
}

const appStore = useAppStore()
const { currentSystemVersion } = storeToRefs(appStore)

const currentSystemVersionId = computed(() => currentSystemVersion.value?.systemVersion?.systemVersionId)
const currentVersionStr = computed(() => currentSystemVersion.value?.systemVersion?.systemVersion ?? "0.0.0")

/** 只展示已应用到系统的版本：版本号不大于当前版本（semver）；按版本号降序 */
const filteredVersionList = computed(() => {
  const cur = currentVersionStr.value
  return systemVersionList.value
    .filter(item => {
      const v = item.systemVersion ?? "0.0.0"
      if (semver.gt(v, cur)) return false
      return true
    })
    .sort((a, b) => semver.compare(b.systemVersion ?? "0.0.0", a.systemVersion ?? "0.0.0"))
})

// 监听对话框显示状态，自动加载数据
watch(dialogVisible, (visible) => {
  if (visible) {
    currentPage.value = 1
    getVersionHistory()
  }
})
</script>

<style scoped>
.version-list-scroll {
  max-height: 70vh;
  padding-right: 8px;
  overflow-y: auto;
}
</style>
